import { Component, OnInit, Input, Output, EventEmitter, Pipe, PipeTransform } from '@angular/core';
import { Role } from '../../../shared/models/role.model';
import { MatDialog } from '@angular/material/dialog';
import { RoleGroupMember } from '../../../shared/models/role-group-member.model';
import { RoleDetailsDialog } from '../../../shared/dialogs/role-details/role-details.dialog';
import { RoleService } from '../../../shared/services/role.service';
import { ConfirmationDialogComponent, ConfirmationDialogModel } from '../../../shared/dialogs/confirmation/confirmation-dialog.component';
import { AccessibilityHelper } from '../../../shared/accessibility.helper';
import { CurrentSiteService } from '../../../shared/services/current-site.service';
import { FolderPermissionService } from '../../../shared/services/folder-permission.service';
import { SiteLevelRoleMember } from '../../../shared/models/site-level-role-member.model';

@Pipe({
    name: 'FilterRoleMembers',
    standalone: false
})
export class FilterRoleMembersPipe implements PipeTransform {
    transform(value: any, roleId: number): any {
        if (!value) return value;
        return value.filter((rm) => rm.roleId == roleId);
    }
}

@Component({
    selector: 'app-permission-roles',
    templateUrl: './permission-roles.component.html',
    styleUrls: ['./permission-roles.component.scss'],
    standalone: false
})

export class PermissionRolesComponent implements OnInit {
    // Inputs.
    @Input() roles: Role[];
    @Input() membersInRoles: Array<any>;
    @Input() title: string = 'NA';

    @Output() editRoleMembers: EventEmitter<Role> = new EventEmitter<Role>();

    // Constructor.
    public constructor(public dialog: MatDialog, private roleService: RoleService, private currentSiteService: CurrentSiteService, private folderPermissionService: FolderPermissionService) {
    }

    // Life cycle methods.
    public ngOnInit(): void {
    }

    // Defined methods called my my .html code.
    public get membersAreInherited(): boolean {
        return false;
    }

    public get SiteLevelRoles(): Role[] {
        return this.roles.filter(x => { return x.isSiteLevel });
    }
    public getSiteRoleMembers(role: Role): SiteLevelRoleMember[] {
        return role.siteLevelRoleMembers;
    }
    public siteLevelRoleMemberCount(role: Role): number {
        return role.countOfSiteLevelMembers();
    }
    public get FolderLevelRoles(): Role[] {
        return this.roles.filter(x => { return !x.isSiteLevel });
    }
    public get MembersInRoles(): Array<any> {
        return this.membersInRoles;
    }

    public folderLevelRoleTotalMemberCountText(role: Role): string {
        return role.memberCount() == 1 ? '1 member total' : `${role.memberCount()} total members`;
    }

    public getTitleForManageRolesIcon() {
        return "Manage Role Members";
    }

    // Handle control events.
    public openRoleDrawer(data: any): void {
        AccessibilityHelper.preventDoubleHitOnEvent(data.event);
        data.event.stopPropagation();

        // Note:  if this is a site-level role, emit an output telling my parent to edit this role's membership.
        //        If this is a folder-level role, display a message directing the user to the folders tab.
        // Simply pass this event on to my parent component.
        let role: Role = data.role;

        if (role.isSiteLevel) {
            this.editRoleMembers.emit(role);
        } else {
            // Note:  folder-level role members are now edited on the "Folders, Roles, and Members" tab of this component.
        }
    }
    public deleteRole(event: any, role: any): void {
        this.deleteOnConfirm(role, () => {
            this.roleService.delete(role.id).then(res => {
                this.roles = this.roles.filter(r => r.id != role.id);
                this.currentSiteService.deleteRole(role);
            });
        });
    }
    public openCreateRoleDialog() {
        const dialogRef = this.dialog.open(RoleDetailsDialog, { data: { showSiteLevelRoleWarning: false, defaultToFolderlevel: false } });
        dialogRef.afterClosed().subscribe(newRole => {
            if (newRole != null) {
                newRole.name = newRole.name.trim();
                if (newRole.isSiteLevel) {
                    if (this.SiteLevelRoles.filter(r => r.name == newRole.name).length > 0) {
                        return;
                    }
                }
                else {
                    if (this.FolderLevelRoles.filter(r => r.name == newRole.name).length > 0) {
                        return;
                    }
                }
                newRole.dataCollectionId = this.currentSiteService.Site.id;
                this.roleService.create(newRole).then(res => {
                    this.roles.push(res);
                    this.currentSiteService.addRole(res as Role);
                });
            }
        });
    }
    public openEditRoleDialog(data: any): void {
        let origIsSiteLevel = data.role.isSiteLevel;
        const dialogRef = this.dialog.open(RoleDetailsDialog, {
            data: {
                role: data.role
            },
            disableClose: true
        });

        dialogRef.afterClosed().subscribe(role => {
            if (role != null) {
                this.doUpdateRole(role);
            }
        });
    }

    // Helper methods.
    private doUpdateRole(role: any) {
        this.roleService.update(role).then(res => {
            let roles = this.roles.filter(x => x.id != res.id);
            this.currentSiteService.updateRole(role);
        });
    }

    private deleteOnConfirm(role: Role, callback: Function) {
        if (role.memberCount() > 0) {
            alert("You cannot delete a Role that has members. Remove the members from this role first, then delete it.");
            return;
        }
        let dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: new ConfirmationDialogModel(`Delete Role`, `Are you sure you want to delete ${role.name} Role? The operation cannot be undone.`)
        });
        dialogRef.afterClosed().subscribe(yes => {
            if (yes) callback();
        });
    }
}
